import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import {
  adminApi,
  type AffiliateNetwork,
  type CommissionType,
  type ProgramStatus,
} from '../../services/adminApi';
import { Seo } from '../../components/seo/Seo';

const PAGE_SIZE = 25;

const NETWORKS: AffiliateNetwork[] = [
  'direct',
  'impact',
  'avantlink',
  'cj',
  'awin',
  'partnerize',
  'other',
];
const COMMISSION_TYPES: CommissionType[] = [
  'percentage',
  'flat_per_order',
  'flat_per_item',
  'tiered',
];
const STATUSES: ProgramStatus[] = ['pending', 'active', 'paused', 'terminated'];

type StatusFilter = '' | ProgramStatus;

function statusColor(status: ProgramStatus): 'success' | 'warning' | 'default' | 'error' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  if (status === 'terminated') return 'error';
  return 'default';
}

/**
 * Admin affiliate-program index page (ADR 0006, GitHub #12 affiliate vertical).
 *
 * Lists per-retailer affiliate programs with network / status / click +
 * conversion counts, a status filter, and a create form (one program per
 * retailer). Auth is enforced upstream by AdminLayout; the server re-checks
 * admin role and audit-logs each mutation.
 */
export function AffiliatesListPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);

  // Create-form state.
  const [retailerId, setRetailerId] = useState('');
  const [network, setNetwork] = useState<AffiliateNetwork>('direct');
  const [commissionType, setCommissionType] = useState<CommissionType>('percentage');
  const [rate, setRate] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'affiliates', { statusFilter, page }],
    queryFn: () =>
      adminApi.listAffiliatePrograms({
        status: statusFilter === '' ? undefined : statusFilter,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createAffiliateProgram({
        retailerId: retailerId.trim(),
        network,
        commissionType,
        commissionRateBps:
          commissionType === 'percentage' && rate !== ''
            ? Math.round(Number(rate) * 100)
            : undefined,
      }),
    onSuccess: async () => {
      setRetailerId('');
      setRate('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
    },
  });

  function onStatusFilterChange(event: SelectChangeEvent<string>) {
    setStatusFilter(event.target.value as StatusFilter);
    setPage(1);
  }

  function onCreateSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!retailerId.trim()) return;
    createMutation.mutate();
  }

  return (
    <Box>
      <Seo title="Affiliate programs" noindex />
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Affiliate programs
      </Typography>

      <Paper
        component="form"
        onSubmit={onCreateSubmit}
        sx={{ p: 2, mb: 3, display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
        elevation={0}
        variant="outlined"
        aria-label="Create affiliate program"
      >
        <TextField
          id="affiliate-retailer-id"
          label="Retailer ID"
          size="small"
          required
          value={retailerId}
          onChange={(e) => setRetailerId(e.target.value)}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="affiliate-network-label">Network</InputLabel>
          <Select
            labelId="affiliate-network-label"
            id="affiliate-network"
            value={network}
            label="Network"
            onChange={(e) => setNetwork(e.target.value as AffiliateNetwork)}
          >
            {NETWORKS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="affiliate-commission-type-label">Commission type</InputLabel>
          <Select
            labelId="affiliate-commission-type-label"
            id="affiliate-commission-type"
            value={commissionType}
            label="Commission type"
            onChange={(e) => setCommissionType(e.target.value as CommissionType)}
          >
            {COMMISSION_TYPES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {commissionType === 'percentage' && (
          <TextField
            id="affiliate-rate"
            label="Rate (%)"
            size="small"
            type="number"
            inputProps={{ min: 0, step: 0.1 }}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            sx={{ minWidth: 120 }}
          />
        )}
        <Button type="submit" variant="contained" disabled={createMutation.isPending}>
          Create program
        </Button>
      </Paper>

      {createMutation.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Create failed: {(createMutation.error as Error).message}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1 }} elevation={0} variant="outlined">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="affiliate-status-filter-label">Status</InputLabel>
          <Select
            labelId="affiliate-status-filter-label"
            id="affiliate-status-filter"
            value={statusFilter}
            label="Status"
            onChange={onStatusFilterChange}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading affiliate programs" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load affiliate programs: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Affiliate programs">
              <TableHead>
                <TableRow>
                  <TableCell>Retailer ID</TableCell>
                  <TableCell>Network</TableCell>
                  <TableCell>Commission</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Clicks</TableCell>
                  <TableCell>Conversions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.programs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        No affiliate programs match the current filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.programs.map((program) => (
                    <TableRow key={program.id} hover>
                      <TableCell>{program.retailerId}</TableCell>
                      <TableCell>{program.network}</TableCell>
                      <TableCell>{program.commissionType}</TableCell>
                      <TableCell>
                        <Chip
                          label={program.status}
                          color={statusColor(program.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{program.clickCount ?? 0}</TableCell>
                      <TableCell>{program.conversionCount ?? 0}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            component={RouterLink}
                            to={`/admin/affiliates/${program.id}`}
                            aria-label={`Manage affiliate program ${program.retailerId}`}
                          >
                            Manage
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Affiliate programs pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default AffiliatesListPage;
